import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import type { CampaignRecipient } from '@otw/models/leads/marketingCampaign'
import {
  CAMPAIGN_RECIPIENT_OUTCOME_LABEL,
  resolveCampaignRecipientOutcome,
} from '@otw/models/leads/marketingCampaign'
import React, { useMemo } from 'react'
import { useCampaignRecipients } from 'hooks/contacts/useCampaignRecipients'

const formatWhen = (value: Date | null | undefined): string => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '—'
  return value.toLocaleString('en-US')
}

interface CampaignRecipientsDialogProps {
  open: boolean
  onClose: () => void
  campaignId: string | null
  campaignName: string
}

const CampaignRecipientsDialog: React.FC<CampaignRecipientsDialogProps> = ({
  open,
  onClose,
  campaignId,
  campaignName,
}) => {
  const { data: recipients, isLoading } = useCampaignRecipients(
    open ? campaignId ?? undefined : undefined,
  )

  const columns = useMemo<GridColDef<CampaignRecipient>[]>(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: 'outcome',
        headerName: 'Status',
        width: 140,
        valueGetter: (_value, row) =>
          CAMPAIGN_RECIPIENT_OUTCOME_LABEL[resolveCampaignRecipientOutcome(row)],
      },
      {
        field: 'sentAt',
        headerName: 'Sent',
        width: 180,
        valueFormatter: (value: Date | null) => formatWhen(value),
      },
      {
        field: 'unsubscribedAt',
        headerName: 'Unsubscribed',
        width: 180,
        valueFormatter: (value: Date | null | undefined) => formatWhen(value ?? null),
      },
      {
        field: 'error',
        headerName: 'Error',
        flex: 1,
        minWidth: 140,
        valueGetter: (_value, row) => row.error?.trim() || '—',
      },
    ],
    [],
  )

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          Recipients
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {campaignName}. Open and click are not available until a full email
          provider is connected.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DataGridPro
          rows={recipients ?? []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          density="compact"
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          sx={{ border: 0, height: 420 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CampaignRecipientsDialog
